import React from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import Layout from "./components/Layout";
import "./index.css";
import "./input.css";
import { Container, Box } from "@mui/material";
import { SignInButton } from "./components/Buttons/SignInButton";
import { Route, Routes, Navigate } from "react-router-dom";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "./authConfig";
import Home from "./pages/Home";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme/index";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ReviewItems from "./pages/ReviewItems";
import History from "./pages/History";
import MySubmissions from "./pages/MySubmissions";
import CM3PMForm from "./pages/CM3PMForm/CM3PMForm";
import CCMForm from "./pages/CCMForm/CCMForm";
import DCCMForm from "./pages/DCCMForm/DCCMForm";
import SPMForm from "./pages/SPMForm/SPMForm";
import Upload from "./pages/Upload";
import Payroll from "./pages/Payroll";
import PayrollFilesPage from "./pages/PayrollFiles";
import UserManualPage from "./pages/UserManual";
import "./styles/theme.css"
import "./styles/global-theme.css"
import "./styles/globals.css"
import "./styles/sub-forms.css"
import { QBAThemeProvider } from "./theme/QBAThemeContext";

// Import Admin Pages
import StatusOverview from "./pages/admin/dashboard/StatusOverview";
import EmployeeHierarchyOverrides from "./pages/admin/manage/EmployeeHierarchyOverrides";
import Applications from "./pages/admin/manage/Applications/Applications";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // Data is considered fresh for 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const AuthWrapper = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  if (inProgress === InteractionStatus.None && !isAuthenticated) {
    instance.loginRedirect(loginRequest);
  }

  return (
    <div className="App">
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Container sx={{ mt: (theme) => theme.spacing(5) }}>
          <Box mx={2}>
            <h5 className="card-title">Please sign-in to access.</h5>
          </Box>
          <SignInButton
            // @ts-ignore
            variant="contained"
          />
        </Container>
      </UnauthenticatedTemplate>
    </div>
  );
};

// This checks roles AFTER authentication
const RoleBasedWrapper = ({
  children,
  requiredRoles,
}: {
  children: React.ReactNode;
  requiredRoles: string[];
}) => {
  const { accounts } = useMsal();

  const roleMap: Record<string, string> = {
    "DCCM-Review": "DCCM",
    "SPM-Review": "SPM",
    "VP-Review": "VP",
    "DM-Review": "DM",
  };

  const userRoles = [
    ...new Set(
      ((accounts[0]?.idTokenClaims?.roles as string[]) || []).map(
        (role) => roleMap[role] || role
      )
    ),
  ].sort();

  const hasAccess = requiredRoles.some((role) => userRoles.includes(role));

  if (!hasAccess) {
    // Redirect to home if user doesn't have required role
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};


export default function App() {
  return (
    <QBAThemeProvider>
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Layout>
              <Routes>
                <Route
                  path="/"
                  element={
                    <AuthWrapper>
                      <Home />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <AuthWrapper>
                      <Home />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/payroll"
                  element={
                    <AuthWrapper>
                      <Payroll />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/payroll/files"
                  element={
                    <AuthWrapper>
                      <PayrollFilesPage />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/help"
                  element={
                    <AuthWrapper>
                      <UserManualPage />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/my-applications"
                  element={
                    <AuthWrapper>
                      <Home />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/review-items"
                  element={
                    <AuthWrapper>
                      <ReviewItems />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/history"
                  element={
                    <AuthWrapper>
                      <History />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/my-submissions"
                  element={
                    <AuthWrapper>
                      <RoleBasedWrapper requiredRoles={["DCCM", "DM", "VP"]}>
                        <MySubmissions />
                      </RoleBasedWrapper>
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/history/:id"
                  element={
                    <AuthWrapper>
                      <Home />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/CM3-PM"
                  element={
                    <AuthWrapper>
                      <CM3PMForm />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/CM3-PM/:id"
                  element={
                    <AuthWrapper>
                      <CM3PMForm />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/CCM"
                  element={
                    <AuthWrapper>
                      <CCMForm />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/CCM/:id"
                  element={
                    <AuthWrapper>
                      <CCMForm />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/DCCM"
                  element={
                    <AuthWrapper>
                      <DCCMForm />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/DCCM/:id"
                  element={
                    <AuthWrapper>
                      <DCCMForm />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/SPM"
                  element={
                    <AuthWrapper>
                      <SPMForm />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/SPM/:id"
                  element={
                    <AuthWrapper>
                      <SPMForm />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/upload"
                  element={
                    <AuthWrapper>
                      <Upload />
                    </AuthWrapper>
                  }
                />

                {/* ========== ADMIN ROUTES WITH ROLE-BASED ACCESS ========== */}
                {/* Dashboard - accessible to Admin, Ops */}
                <Route
                  path="/admin/dashboard/status-overview"
                  element={
                    <AuthWrapper>
                      <RoleBasedWrapper requiredRoles={["Admin", "Ops"]}>
                        <StatusOverview />
                      </RoleBasedWrapper>
                    </AuthWrapper>
                  }
                />

                {/* Management Pages - accessible to Admin, Ops only */}
                <Route
                  path="/admin/manage/applications"
                  element={
                    <AuthWrapper>
                      <RoleBasedWrapper requiredRoles={["Admin", "Ops"]}>
                        <Applications />
                      </RoleBasedWrapper>
                    </AuthWrapper>
                  }
                />

                <Route
                  path="/admin/manage/employee-hierarchy-overrides"
                  element={
                    <AuthWrapper>
                      <RoleBasedWrapper requiredRoles={["Admin", "Ops"]}>
                        <EmployeeHierarchyOverrides />
                      </RoleBasedWrapper>
                    </AuthWrapper>
                  }
                />
                {/* ========== END ADMIN ROUTES ========== */}
              </Routes>
            </Layout>
          </LocalizationProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </QBAThemeProvider>
  );
}
